import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { isEnglishOperatorMode, withOperatorSurface } from "../utils/operatorMode";

const API_URL = window.location.hostname === 'localhost'
    ? (process.env.REACT_APP_API_URL || "http://localhost:3173")
    : window.location.origin;

const SignupThanksPage = () => {
    const isEnglishBeta = isEnglishOperatorMode();
    const [email, setEmail] = useState(() => {
        if (!isEnglishBeta) return '';
        try {
            return window.sessionStorage.getItem('english_beta_signup_email') || '';
        } catch (storageError) {
            return '';
        }
    });
    const [resendStatus, setResendStatus] = useState(null);
    const [isResending, setIsResending] = useState(false);

    const handleResend = async () => {
        if (!email) {
            setResendStatus({ type: 'error', message: 'Enter the email address you used to create the account.' });
            return;
        }

        setIsResending(true);
        setResendStatus(null);
        try {
            const response = await fetch(`${API_URL}/api/resend-verification-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    surface: 'english_beta',
                    operator_surface: 'english_beta',
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(data.error || 'Could not resend the verification email.');
            setResendStatus({
                type: 'success',
                message: 'If the account is waiting for verification, a new email has been sent. Check your inbox and spam folder.',
            });
        } catch (error) {
            setResendStatus({ type: 'error', message: error.message || 'Could not resend the verification email.' });
        } finally {
            setIsResending(false);
        }
    };

    const correctionLink = withOperatorSurface(`/signup${email ? `?email=${encodeURIComponent(email)}` : ''}`);

    return (
        <div className="bg-slate-50 flex items-center justify-center min-h-screen p-3">
            <div className="w-full max-w-2xl mx-auto px-0 sm:px-6 py-8 sm:py-12 text-center">
                <div className="bg-white p-5 sm:p-12 rounded-2xl shadow-xl">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                        <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                    </div>
                    <h1 className="mt-6 text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                        {isEnglishBeta ? "Check your inbox" : "ご登録ありがとうございます"}
                    </h1>
                    <p className="mt-4 max-w-lg mx-auto text-base text-slate-600">
                        {isEnglishBeta ? (
                            <>
                                We sent a verification email to the address you entered.
                                {email && (
                                    <>
                                        <br />
                                        <span className="mt-2 inline-block rounded-lg bg-slate-100 px-3 py-1 font-bold text-slate-900 break-all">
                                            {email}
                                        </span>
                                    </>
                                )}
                                <br />
                                Open the link in that email to complete your English Operator Beta registration.
                                <br />
                                The verification link keeps the English beta surface active through login.
                            </>
                        ) : (
                            <>
                                ご本人様確認のため、ご入力いただいたメールアドレス宛に認証メールを送信しました。
                                <br />
                                メールに記載されているリンクをクリックして、登録を完了してください。
                            </>
                        )}
                    </p>
                    <div className="mt-10">
                        <p className="text-sm text-slate-500">
                            {isEnglishBeta ? "If it does not arrive, check your spam folder, promotions tab, and the spelling of your email address." : "メールが届かない場合は、迷惑メールフォルダもご確認ください。"}
                        </p>
                        {isEnglishBeta ? (
                            <>
                                <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left">
                                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                                        Resend verification email
                                    </label>
                                    <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(event) => setEmail(event.target.value)}
                                            placeholder="Email address"
                                            className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleResend}
                                            disabled={isResending}
                                            className="w-full sm:w-auto rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
                                        >
                                            {isResending ? 'Sending...' : 'Resend'}
                                        </button>
                                    </div>
                                    <p className="mt-3 text-xs leading-5 text-slate-500">
                                        If the address above is wrong, create a new account with the correct email address.
                                    </p>
                                    {resendStatus && (
                                        <p className={`mt-3 text-xs font-semibold ${resendStatus.type === 'success' ? 'text-emerald-700' : 'text-red-600'}`}>
                                            {resendStatus.message}
                                        </p>
                                    )}
                                </div>
                                <div className="mt-4 flex flex-col items-center justify-center gap-3 sm:flex-row">
                                    <Link to={withOperatorSurface("/login")} className="text-indigo-600 hover:underline">Go to login</Link>
                                    <Link to={correctionLink} className="text-slate-600 hover:underline">Wrong email? Create another account</Link>
                                </div>
                            </>
                        ) : (
                            // トップページはReactアプリ外のLPなので、通常の<a>タグを使用
                            <a href="/" className="text-indigo-600 hover:underline mt-4 inline-block">トップページへ戻る</a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignupThanksPage;
